/* eslint-disable no-console */
/**
 * AmitySocialHub - A component for displaying social hub
 *
 * @module views/components/AmitySocialHub
 * @memberof -Common
 */

import './AmitySocialHub.scss';

import React, { useEffect, useRef, useState } from 'react';

import IframeResizer from 'iframe-resizer-react';
import PropTypes from 'prop-types';

import LoaderCircular from '@ulta/core/components/LoaderCircular/LoaderCircular';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage.js';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys.js';

import * as utils from './AmitySocialHub';

/**
 * Represents a AmitySocialHub component
 *
 * @method
 * @param {AmitySocialHubProps} props - React properties passed from composition
 * @returns AmitySocialHub
 */
export const AmitySocialHub = ( props ) => {

  const { profileId, loginAction, invokeAction, fetchAuthTokenAction, authToken, authTokenRequest, community, user, post } = props;
  const { amityIframeUrl, amityMessageOrigin } = useAppConfigContext();
  const [hasLoaded, setHasLoaded] = useState( false );
  const [dailyPrompt, setDailyPrompt] = useState( false );
  const [shouldShowLogin, setShouldShowLogin] = useState( false );
  const [restoreScroll, setRestoreScroll] = useState( false );
  const [savedScrollPosition, setSavedScrollPosition] = useState( 0 );
  const [resetIframeSize, setResetIframeSize] = useState( false );
  const [authTokenProfileId, setAuthTokenProfileId] = useState( null );
  const [fetchAuthToken, setFetchAuthToken] = useState( false );
  const [iframeUrl, setIframeUrl] = useState( null );

  // const iframeUrl = getIframeUrl( { amityIframeUrl, profileId, community }, { useServerRequestContext } );
  const iframeRef = useRef( null );
  const wrapperRef = useRef();

  useEffect( () => {
    utils.handleLoginPrompt( { hasLoaded, loginAction, shouldShowLogin }, { invokeAction, setShouldShowLogin } );
  }, [hasLoaded, loginAction, shouldShowLogin] );

  useEffect( () => {
    utils.handleDailyPrompt( { setDailyPrompt, setShouldShowLogin } );
  }, [dailyPrompt] );

  useEffect( () => {
    utils.handleResetIframeSize( { resetIframeSize, iframeRef }, { setResetIframeSize } );
  }, [resetIframeSize] );

  useEffect( () => {
    utils.updateIframeUrl( { amityIframeUrl, profileId, community, user, post }, { setIframeUrl } );
  }, [profileId] );

  useEffect( () => {
    utils.handleAuthTokenRequest( { authToken, fetchAuthToken, iframeRef, fetchAuthTokenAction, authTokenProfileId },
      { invokeAction } );
  }, [fetchAuthToken, invokeAction] );

  useEffect( () => {
    utils.handleAuthTokenResponse( { iframeRef, authToken, authTokenRequest, fetchAuthToken }, { setFetchAuthToken } );
  }, [authTokenRequest, authToken] );

  useEffect( () => {
    global.scrollTo( 0, savedScrollPosition );
    setRestoreScroll( false );
  }, [restoreScroll] );

  useEffect( () => {
    const handlePostMessage = ( event ) => {
      utils.setupPostMessageListener(
        { amityMessageOrigin, event, wrapperRef, iframeRef },
        { setDailyPrompt, setShouldShowLogin, setHasLoaded, setSavedScrollPosition,
          setRestoreScroll, setFetchAuthToken, setResetIframeSize, setAuthTokenProfileId }
      );
    };

    global.addEventListener( 'message', handlePostMessage );

    return () => {
      global.removeEventListener( 'message', handlePostMessage );
    };
  }, [] );

  const showLoader = !profileId || !hasLoaded;

  return (
    <div className='AmitySocialHub'
      ref={ wrapperRef }
    >
      { showLoader && <div className='AmitySocialHub__loader'><LoaderCircular/></div> }

      { profileId && (
        <IframeResizer
          log
          id='amity-iframe'
          title='Amity'
          className='AmitySocialHub__iframe'
          src={ iframeUrl }
          style={ { width: '100%', height: '100vh' } }
          forwardRef={ iframeRef }
          waitForLoad={ true }
          heightCalculationMethod='taggedElement'
        />
      ) }
    </div>
  );
};

/**
 * this will generate a dynamic iframe url depending on what is passed in the URL
 * groupId - the community to link to directly
 * postId - the specific post to link to directly
 * userId - the user feed to link to directly
 *
 * @param data
 * @returns {string}
 */
export const updateIframeUrl = ( data, methods ) => {

  const {
    amityIframeUrl,
    profileId,
    community,
    user,
    post
  } = data || {};
  const { setIframeUrl } = methods || {};

  if( !profileId ){
    return;
  }

  const url = new URLSearchParams( window.location.search );
  const iframeUrl = new URL( window.location.origin + amityIframeUrl );
  iframeUrl.searchParams.append( 'profileId', profileId );

  if( url.get( 'groupId' ) ){
    iframeUrl.searchParams.append( 'groupId', url.get( 'groupId' ) );
  }
  else if( community?.communityId ){
    iframeUrl.searchParams.append( 'groupId', community.communityId );
  }

  if( url.get( 'postId' ) ){
    iframeUrl.searchParams.append( 'postId', url.get( 'postId' ) );
  }
  else if( post?.postId ){
    iframeUrl.searchParams.append( 'postId', post.postId );
  }

  if( url.get( 'memberId' ) ){
    iframeUrl.searchParams.append( 'memberId', url.get( 'memberId' ) );
  }
  else if( user?.userId ){
    iframeUrl.searchParams.append( 'memberId', user.userId );
  }

  setIframeUrl( iframeUrl );
};

/**
 * sets up message listeners for events that occur throughout the lifecycle of the page....all events will be triggered from
 * the child iframe hosted by social+
 * @param data
 * @param methods
 */
export const setupPostMessageListener = ( data, methods ) => {
  const { amityMessageOrigin, event = {}, wrapperRef = {}, iframeRef = {}, savedScrollPosition } = handleEmptyObjects( data );
  const { setHasLoaded, setShouldShowLogin, setDailyPrompt, setSavedScrollPosition,
    setRestoreScroll, setFetchAuthToken, setResetIframeSize, setAuthTokenProfileId } = methods || {};

  if( event.origin !== amityMessageOrigin || !setShouldShowLogin || !setHasLoaded ){
    return;
  }

  if( event.data === 'explorePageLoaded' || event.data === 'onPageLoaded' ){
    setDailyPrompt( true );
    setHasLoaded( true );
    // initialize default it back to the original
    setResetIframeSize( true );
  }
  else if( event.data === 'uikitRendered' ){
    // Logic to handle after uikit has been rendered
  }
  else if( event.data === 'anonymousNeedsLogin' ){
    setShouldShowLogin( true );
  }
  else if( event.data === 'parentNeedsScrollToTop' ){
    // `parentNeedsScrollToTop` will be posted from iFrame when the Nickname dialog is opened
    global.scrollTo( 0, 0 );
  }
  else if( event.data?.action === 'scrollToTop' ){
    setSavedScrollPosition( window.scrollY );
    setTimeout( () => {
      global.scrollTo( 0, 100 );
    }, 200 );
  }
  else if( event.data?.action === 'restoreScroll' ){
    setRestoreScroll( true );
  }
  else if( event.data?.action === 'fetchAuthToken' ){
    setAuthTokenProfileId( event.data.profileId );
    setFetchAuthToken( true );
  }
};

/**
 * Handles the authtoken response from DXL...triggers event that sends the authtoken back to the child iframe
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const handleAuthTokenResponse = ( data, methods ) => {
  const { authToken, iframeRef, fetchAuthToken } = data || {};
  const { setFetchAuthToken } = methods || {};

  // no need to process since auth token never requested
  if( !fetchAuthToken ){
    return;
  }

  iframeRef?.current?.sendMessage( { data:'authToken', token: authToken } );
  setFetchAuthToken( false );
};

/**
 * handles the authToken request from the child iframe....initiates a DXL action that calls the AmitySocialHub query
 * which returns the latest authToken based on the profileId passed in
 * @param data
 * @param methods
 */
export const handleAuthTokenRequest = ( data, methods ) => {

  const { fetchAuthToken, iframeRef, authTokenProfileId, fetchAuthTokenAction } = data || {};
  const { invokeAction } = methods || {};

  if( !fetchAuthToken ){
    return;
  }

  if( iframeRef && iframeRef.current && fetchAuthToken ){
    // get another token for the specific profile id
    const authTokenLoginActionWithProfileId = { ...fetchAuthTokenAction,
      variables: { moduleParams: { testKey: Math.random(), profileId: authTokenProfileId, loginStatus: 'anonymous', authTokenRequest: new Date().getTime() } }
    };
    invokeAction( authTokenLoginActionWithProfileId );
  }
};

/**
 * programmtically fires off the resize of the iframe...triggered when user returns to the explore page
 * @param data
 * @param methods
 */
export const handleResetIframeSize = ( data, methods ) => {
  const { resetIframeSize, iframeRef } = data || {};
  const { setResetIframeSize } = methods || {};

  if( !resetIframeSize ){
    return;
  }

  document.getElementById( 'amity-iframe' ).style.height = '1375px';
  // resize when user returns to explore page
  iframeRef.current?.resize();
  setResetIframeSize( false );
};

/**
 * Handles login prompt behavior
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const handleLoginPrompt = ( data, methods ) => {
  const { hasLoaded, loginAction, shouldShowLogin } = data || {};
  const { invokeAction, setShouldShowLogin } = methods || {};

  if( !hasLoaded || !loginAction || !shouldShowLogin ){
    return;
  }
  const fakeloginAction = { ...loginAction,
    variables: { moduleParams: { testKey: Math.random() } }
  };
  invokeAction( fakeloginAction );
  setShouldShowLogin( false );
};

/**
 * Handles daily prompt timer
 * @param {object} methods - Methods
 */
export const handleDailyPrompt = ( methods ) => {

  const { setDailyPrompt, setShouldShowLogin } = methods || {};

  let lastAmityPrompt = getStorage( { secure: false, key: STORAGE_KEY.amityPrompt } ) || {};

  if( !lastAmityPrompt?.lastPrompt || ( Date.now() - lastAmityPrompt.lastPrompt > 864000 ) ){
    lastAmityPrompt = Date.now();
    const lastPromptData = { lastPrompt : lastAmityPrompt };
    setStorage( {
      secure: false,
      key: STORAGE_KEY.amityPrompt,
      value: lastPromptData
    } );
    setDailyPrompt( false );
    setShouldShowLogin( true );
  }
};

/**
 * Property type definitions
 * @typedef AmitySocialHubProps
 * @type {object}
 * @property {string} omniCustId - userId
 */
export const propTypes = {
  omniCustId: PropTypes.string
};

AmitySocialHub.propTypes = propTypes;

export default AmitySocialHub;